import { Box, Button, Typography } from '@mui/material';
import { ApplicationRoutes } from '../../../application/constants/routes';
import { useI18n } from '../../../application/language/useI18n';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ApplicationInfoWarning } from '../../admin/page/projectRole/entity/components/ApplicationInfoWarning';
import { PageEmptyContent } from '../../design/page/PageEmptyContent';
import { PageLoader } from '../../design/page/PageLoader';
import { ApplicationDetailDomain } from '../page/application/info/store/ApplicationDetailDomain';

import { UserRating } from '../../admin/page/usersRating/UserRating';
import space from './assets/space.png';
import { UnitListAdminCard } from './parts/UnitListAdminCard';
import { UnitListUserCard } from './parts/UnitListUserCard';
import { Colors } from '../../design/color/Colors';

export const LearningApplicationInfoPage = observer(({ domain }: { domain: ApplicationDetailDomain }) => {
  const { applicationId = '' } = useParams<any>();
  const { translate } = useI18n();
  const editSpecsSignature = JSON.stringify(domain.ui.editApplication.entity.specificationsIds || []);
  const isFirstRender = React.useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    domain.regroupLearning();
  }, [editSpecsSignature]);

  useEffect(() => {
    return () => {
      domain.dropFilterByRole();
    };
  }, [domain]);

  const renderUserUnits = (group) =>
    group.unitsList.required.map((unit) => {
      const unitResult = domain.ui.learningPassingResultList.list.filter(
        (unitRes) => unitRes.unitId === unit.id,
      )[0] || {
        totalUnitResult: 0,
        isPassed: false,
      };
      return (
        <UnitListUserCard
          key={unit.id}
          card={unit}
          action={domain.redirect}
          score={unit.score}
          passingUnitResult={unitResult}
        />
      );
    });

  const UserNoUnitsComponents = () => (
    <Box width={'100%'} sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box width={'auto'} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '8px' }}>
        <img src="/pictures/sphere-nexus.png" />
        <Typography textAlign={'center'} typography={'h6'} marginTop="16px">
          {translate('phrases.trainingNotSetYet')}
        </Typography>
        <Typography
          textAlign={'center'}
          typography="body2"
          marginTop="8px"
          marginBottom="16px"
          maxWidth="300px"
          fontSize="12px"
          sx={{ opacity: 0.7 }}
        >
          {translate('phrases.noTrainingForThisSystem')}
        </Typography>
        <a href={ApplicationRoutes.LearningRoot}>
          <Button
            sx={{
              background: Colors.primary,
              color: Colors.textOnPrimary,
              '&:hover': {
                backgroundColor: Colors.primaryHover,
              },
            }}
          >
            {translate('phrases.toLearning')}
          </Button>
        </a>
      </Box>
    </Box>
  );

  const renderAdminUnits = (group, type) => {
    if (!group.unitsList[type].length) {
      return null;
    }

    return (
      <Box sx={{ marginBottom: '16px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ marginBottom: '16px', color: Colors.textSecondary }}>
            {translate(type === 'required' ? 'phrases.mandatory' : 'phrases.optional')}
          </Typography>
          <Box
            sx={{
              width: '32px',
              height: '24px',
              padding: '4px, 6px, 4px, 6px',
              borderRadius: '32px',
              border: `solid 1px ${Colors.border}`,
              display: 'flex',
              justifyContent: 'center',
              color: Colors.textSecondary,
            }}
          >
            {group.unitsList[type].length}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {group.unitsList[type].map((unit) => (
            <UnitListAdminCard
              key={unit.id}
              isRequired={type === 'required'}
              unit={unit}
              applicationId={applicationId}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const showNoUnitsRecommended =
    !domain.ui.isAdmin.value &&
    !domain.ui.learningGroupedUnits.list.some((group) => group.unitsList.required.length > 0);

  return (
    <>
      {domain.ui.learningIsLoading.value && (
        <Box sx={{ padding: '24px 15px', minHeight: '100%' }}>
          <PageLoader minHeight="100%" />
        </Box>
      )}
      {!domain.ui.learningIsLoading.value && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '64px', padding: '24px 15px' }}>
          {domain.ui.learningUnitList.list.length > 0 ? (
            <>
              <Typography variant="h4">
                {translate(`phrases.${domain.ui.isAdmin.value ? 'teamCompetencyMap' : 'competencyMap'}`)}
              </Typography>
              {domain.ui.isAdmin.value && (
                <>
                  {domain.ui.learningRolesWithUnits.list.length !== 0 ? (
                    <Box sx={{ display: 'flex', marginTop: '16px', flexWrap: 'wrap' }}>
                      {domain.ui.learningRolesWithUnits.list
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((role) => (
                          <Box
                            key={role.roleId}
                            onClick={() => domain.filterLearningByRole(role.roleId)}
                            sx={{
                              height: '28px',
                              padding: '4px 12px',
                              marginLeft: '10px',
                              borderRadius: '48px',
                              backgroundColor: domain.ui.learningRoleFilterIds.list.includes(role.roleId)
                                ? Colors.primaryActive
                                : Colors.bgSurface,
                              color: Colors.textPrimary,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              fontSize: '12px',
                              cursor: 'pointer',
                              '&:first-of-type': {
                                marginLeft: 0,
                              },
                            }}
                          >
                            {role.name}
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <ApplicationInfoWarning
                      warningText={translate('phrases.noMandatoryUnitsForTheRole')}
                      description={translate('phrases.assignUnitsMessage')}
                      btnText={translate('phrases.setupProjectRoles')}
                      redirectTo={() => domain.redirectTo('/settings/user/project/role/entity')}
                    />
                  )}
                  {domain.ui.learningIsShowWarning.value && (
                    <ApplicationInfoWarning
                      warningText={translate('phrases.profileCharacteristicsErrorMessage1')}
                      description={translate('phrases.profileCharacteristicsErrorMessage2')}
                      btnText={translate('phrases.fillOutTheProfile')}
                      redirectTo={() => (domain.ui.tabsData.value.state = 0)}
                    />
                  )}
                </>
              )}
              <Box
                sx={{
                  display: showNoUnitsRecommended ? 'block' : 'grid',
                  gridTemplateColumns: 'repeat(3,1fr)',
                  columnGap: '24px',
                  rowGap: '8px',
                  marginTop: '24px',
                }}
              >
                {domain.ui.learningGroupedUnits.list.length > 0
                  ? domain.ui.learningGroupedUnits.list.map((group) => {
                      const hasUnits = group.unitsList.required.length > 0 || group.unitsList.notRequired.length > 0;
                      const shouldShowNotRequiredUnitsToAdmin =
                        domain.ui.learningRoleFilterIds.list.length > 0 && !group.unitsList.required.length;
                      const shouldShowCardToUser = !domain.ui.isAdmin.value && !group.unitsList.required.length;
                      const filterPicked = !!domain.ui.learningRoleFilterIds.list.length;

                      if (!hasUnits || shouldShowNotRequiredUnitsToAdmin || shouldShowCardToUser) {
                        return null;
                      }

                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }} key={group.groupName}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ marginBottom: '16px', fontSize: '20px' }}>{group.groupName}</Typography>
                            <Box
                              sx={{
                                width: '32px',
                                height: '24px',
                                padding: '4px, 6px, 4px, 6px',
                                borderRadius: '32px',
                                backgroundColor: Colors.bgSurface,
                                display: 'flex',
                                justifyContent: 'center',
                                color: Colors.textSecondary,
                              }}
                            >
                              {filterPicked
                                ? group.unitsList.required.length
                                : group.unitsList.required.concat(group.unitsList.notRequired).length}
                            </Box>
                          </Box>

                          {domain.ui.isAdmin.value ? (
                            <>
                              {renderAdminUnits(group, 'required')}
                              {!filterPicked && renderAdminUnits(group, 'notRequired')}
                            </>
                          ) : group.unitsList.required.length > 0 ? (
                            renderUserUnits(group)
                          ) : null}
                        </Box>
                      );
                    })
                  : null}
                {showNoUnitsRecommended && <UserNoUnitsComponents />}
              </Box>
            </>
          ) : (
            <NoUnitsDisplay isAdmin={domain.ui.isAdmin.value} domain={domain} />
          )}
          {domain.ui.isAdmin.value && <TeamDisplay domain={domain} />}
        </Box>
      )}
    </>
  );
});

const NoUnitsDisplay = ({ isAdmin, domain }) => {
  const { translate } = useI18n();

  return (
    <Box sx={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <Typography variant="h4">
        {translate(isAdmin ? 'phrases.teamCompetencyMap' : 'userCardInfo.noAssignedUnits')}
      </Typography>
      {isAdmin && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 0px',
            gap: '24px',
          }}
        >
          <img src={space} alt="space" />
          <Typography variant="h5">{translate('phrases.noSuitableUnits')}</Typography>
          <Button
            size="large"
            variant="contained"
            sx={{ background: Colors.primary }}
            onClick={() => domain.redirectToKnowledge()}
          >
            {translate('phrases.goToLearning')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const TeamDisplay = ({ domain }) => {
  const { translate } = useI18n();

  const teamList = domain.ui.learningTeamUserIds.list || [];

  if (teamList.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <PageEmptyContent text={translate('projectPage.noTeamForProject')} isFullPage={false} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <Typography variant="h4">{translate('phrases.team')}</Typography>
      <UserRating includedTeam={teamList} layoutDomain={domain.layoutDomain} />
    </Box>
  );
};
